import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    IconDownload,
    IconId,
    IconPrinter,
    IconRefresh,
} from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import DatePickerField from '@/components/common/DatePickerField';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { recordToolUsage } from '../../../../../services/toolsService';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import {
    deriveToolStatus,
    getToolStatusText,
} from '../../toolPageUtils';
import { usePageZoom } from '../shared/usePagePreviewScale';
import MissingFieldsDialog from '../shared/MissingFieldsDialog';
import GeoSelectField from '../shared/GeoSelectField';
import { geoCascade } from '../shared/bdGeo';
import UnderageNoticeDialog from '../shared/UnderageNoticeDialog';
import { isUnderVoterAge } from '../shared/voterAgeRules';
import VoterRegistrationPreviewFrame from './VoterRegistrationPreviewFrame';
import PagePreviewZoom from '../shared/PagePreviewZoom';
import VoterRegistrationFormInfoBox from './VoterRegistrationFormInfoBox';
import {
    BLOOD_GROUPS,
    DISABILITIES,
    GENDERS,
    INITIAL_VOTER_REGISTRATION_FORM,
    MARITAL_STATUSES,
    RELIGIONS,
    RMO_TYPES,
    SPOUSE_KINDS,
    displayField,
    formatDateField,
    getEmptyVoterRegistrationFields,
    getMobileValidationError,
    getNidValidationError,
    getVoterRegistrationFormSnapshot,
    isVoterRegistrationFormComplete,
    normalizeBdPhone,
    normalizeDigits,
    parseDateField,
    saveVoterRegistrationFormSnapshot,
} from './voterRegistrationFormEngine';
import { downloadVoterRegistrationPdf, printVoterRegistrationForm } from './generateVoterRegistrationPdf';

const inputBaseClass = 'w-full rounded-xl border bg-white px-3 py-2.5 text-[14px] text-[#3D3833] outline-none transition-colors focus:ring-2 focus:ring-purple-400/30 dark:bg-zinc-900 dark:text-zinc-100';
const inputOkClass = 'border-[#ECE8E4] focus:border-purple-400 dark:border-zinc-700';
const inputErrClass = 'border-red-500 dark:border-red-500';

/** Calendar range: old enough for any voter's birth date, and no future days. */
const TODAY = new Date();
const BIRTH_FROM_YEAR = 1900;

/** Numbers that are checked as they are typed, not only on submit. */
const MOBILE_FIELDS = ['telephone', 'mobile'];
const NID_FIELDS = ['fatherNid', 'motherNid', 'spouseNid', 'identifierNid', 'collectorNid'];

function FormLabel({ children, htmlFor }) {
    return (
        <label htmlFor={htmlFor} className="mb-1 block text-[12px] font-semibold text-[#6B635C] dark:text-zinc-400">
            {children}
        </label>
    );
}

function FormSection({ number, title, hint, children }) {
    return (
        <div className="rounded-2xl border border-purple-200 bg-white px-4 py-4 dark:border-purple-900/40 dark:bg-zinc-900">
            <h3 className="mb-1 text-[13.5px] font-bold text-purple-700 dark:text-purple-300">
                {number}. {title}
            </h3>
            {hint ? (
                <p className="mb-3 text-[11.5px] leading-relaxed text-[#6B635C] dark:text-zinc-400">{hint}</p>
            ) : <div className="mb-3.5" />}
            {children}
        </div>
    );
}

function FormInput({
    id, name, value, onChange, onBlur, placeholder, error,
    inputMode, type = 'text', maxLength, autoComplete, disabled,
}) {
    const hasError = Boolean(error);
    return (
        <div>
            <input
                id={id}
                name={name}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onBlur={onBlur}
                placeholder={placeholder}
                inputMode={inputMode}
                type={type}
                maxLength={maxLength}
                autoComplete={autoComplete}
                disabled={disabled}
                className={`${inputBaseClass} ${hasError ? inputErrClass : inputOkClass} ${disabled ? 'opacity-50' : ''}`}
            />
            {hasError && typeof error === 'string' && error.trim() ? (
                <p className="mt-1 text-[11px] text-red-600 dark:text-red-400">{error}</p>
            ) : null}
        </div>
    );
}

export default function VoterRegistrationFormGeneral() {
    const { t } = useTranslation();
    const previewRef = useRef(null);
    const pageZoom = usePageZoom();
    // Which blank the preview should be showing: the one being typed into.
    const [focusField, setFocusField] = useState('');
    const snapshotTimerRef = useRef(null);

    const [form, setForm] = useState(INITIAL_VOTER_REGISTRATION_FORM);
    const [downloading, setDownloading] = useState(false);
    const [confirmingDownload, setConfirmingDownload] = useState(false);
    // Under-18 birth date: a warning, not a block. 'picked' fires the moment the
    // date is chosen, 'download' asks again on the way out — a date typed into a
    // restored draft never passed through the picker.
    const [ageNotice, setAgeNotice] = useState(null);
    const [printing, setPrinting] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});
    const [numberErrors, setNumberErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [hasSavedSnapshot, setHasSavedSnapshot] = useState(false);

    useEffect(() => {
        if (typeof window !== 'undefined') {
            setHasSavedSnapshot(!!getVoterRegistrationFormSnapshot());
        }
    }, []);

    const update = useCallback((key, value) => {
        setForm((prev) => {
            const next = { ...prev, [key]: value };
            window.clearTimeout(snapshotTimerRef.current);
            if (displayField(next.nameBn) || displayField(next.nameEn)) {
                snapshotTimerRef.current = window.setTimeout(() => {
                    saveVoterRegistrationFormSnapshot(next);
                    setHasSavedSnapshot(true);
                }, 400);
            }
            return next;
        });
        setFieldErrors((prev) => {
            if (!prev[key]) return prev;
            if (!displayField(value)) return prev;
            const next = { ...prev };
            delete next[key];
            return next;
        });
    }, []);

    /**
     * Numbers are re-checked as they are typed, but only once the field has
     * been left alone at least once: nobody wants "too short" while they are
     * still halfway through a 17-digit NID.
     */
    const checkNumber = useCallback((key, value) => {
        let message = '';
        if (MOBILE_FIELDS.includes(key)) message = getMobileValidationError(value, t);
        else if (NID_FIELDS.includes(key)) message = getNidValidationError(value, t);
        setNumberErrors((prev) => {
            if (!message && !prev[key]) return prev;
            const next = { ...prev };
            if (message) next[key] = message;
            else delete next[key];
            return next;
        });
        return message;
    }, [t]);

    const handleDigitsChange = (key, raw, maxLength) => {
        const digits = normalizeDigits(raw, maxLength);
        update(key, digits);
        if (touched[key]) checkNumber(key, digits);
    };

    const handlePhoneChange = (key, raw) => {
        const digits = normalizeBdPhone(raw);
        update(key, digits);
        if (touched[key]) checkNumber(key, digits);
    };

    const markTouched = (key) => {
        setTouched((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
        checkNumber(key, form[key]);
    };

    /**
     * Remember what was typed, then export — whatever state the form is in.
     *
     * Missing fields deliberately do NOT block the download: a common use of
     * this tool is printing the sheet blank, or half-filled, to finish by hand
     * at the registration centre. Downloading one only asks for a confirmation
     * that names what is still empty, and printing does not even ask.
     */
    const prepareForExport = () => {
        setFieldErrors({});
        saveVoterRegistrationFormSnapshot(form);
        setHasSavedSnapshot(true);
    };

    const emptyFields = useMemo(() => getEmptyVoterRegistrationFields(form, t), [form, t]);

    const runDownloadPdf = async () => {
        prepareForExport();
        if (!previewRef.current) {
            notifications.show({
                title: t('common.error'),
                message: t('voterRegistrationForm.errors.previewNotReady'),
                color: 'red',
            });
            return;
        }

        setDownloading(true);
        try {
            recordToolUsage('general/voter-registration-form', { category: 'general' });
            const base = form.nameEn.trim().replace(/\s+/g, '-').slice(0, 40)
                || form.nameBn.trim().replace(/\s+/g, '-').slice(0, 40)
                || 'application';
            await downloadVoterRegistrationPdf(previewRef.current, `voter-registration-${base}.pdf`);
            notifications.show({
                title: t('common.success'),
                message: t('voterRegistrationForm.downloadSuccess'),
                color: 'green',
            });
        } catch (err) {
            notifications.show({
                title: t('common.error'),
                message: err?.message || t('voterRegistrationForm.errors.pdfFailed'),
                color: 'red',
            });
        } finally {
            setDownloading(false);
        }
    };

    const birthDate = parseDateField(form.birthDate);
    const underVoterAge = isUnderVoterAge(birthDate);

    const handleDownloadPdf = () => {
        if (underVoterAge) {
            setAgeNotice({ mode: 'download', date: birthDate });
            return;
        }
        if (emptyFields.length) {
            setConfirmingDownload(true);
            return;
        }
        runDownloadPdf();
    };

    const confirmDownloadPdf = () => {
        setConfirmingDownload(false);
        runDownloadPdf();
    };

    const handlePrint = async () => {
        // Printing is an export too — the same under-18 word of warning applies.
        if (underVoterAge) {
            setAgeNotice({ mode: 'print', date: birthDate });
            return;
        }
        await runPrint();
    };

    const runPrint = async () => {
        recordToolUsage('general/voter-registration-form', { category: 'general' });
        prepareForExport();
        if (!previewRef.current) {
            notifications.show({
                title: t('common.error'),
                message: t('voterRegistrationForm.errors.previewNotReady'),
                color: 'red',
            });
            return;
        }

        setPrinting(true);
        try {
            await printVoterRegistrationForm(previewRef.current, t('voterRegistrationForm.errors.popupBlocked'));
            notifications.show({
                title: t('voterRegistrationForm.printTitle'),
                message: t('voterRegistrationForm.printSuccess'),
                color: 'blue',
            });
        } catch (err) {
            notifications.show({
                title: t('common.error'),
                message: err?.message || t('voterRegistrationForm.errors.printFailed'),
                color: 'red',
            });
        } finally {
            setPrinting(false);
        }
    };

    const handleClear = () => {
        setForm(INITIAL_VOTER_REGISTRATION_FORM);
        setFieldErrors({});
        setNumberErrors({});
        setTouched({});
        notifications.show({
            title: t('voterRegistrationForm.clearedTitle'),
            message: t('voterRegistrationForm.clearedMessage'),
            color: 'gray',
        });
    };

    const handleRestoreSnapshot = () => {
        const snapshot = getVoterRegistrationFormSnapshot();
        if (!snapshot) {
            notifications.show({
                title: t('voterRegistrationForm.noSnapshotTitle'),
                message: t('voterRegistrationForm.noSnapshotMessage'),
                color: 'gray',
            });
            return;
        }

        setForm(snapshot);
        setFieldErrors({});
        setNumberErrors({});
        setTouched({});
        notifications.show({
            title: t('voterRegistrationForm.restoredTitle'),
            message: t('voterRegistrationForm.restoredMessage'),
            color: 'green',
        });
    };

    const hasAnyInput = useMemo(
        () => Object.entries(form).some(([key, value]) => (
            typeof INITIAL_VOTER_REGISTRATION_FORM[key] === 'boolean' ? Boolean(value) : displayField(value)
        )),
        [form],
    );

    const isComplete = useMemo(() => isVoterRegistrationFormComplete(form, t), [form, t]);

    const status = deriveToolStatus({
        hasInput: hasAnyInput,
        canRun: isComplete,
        hasResult: isComplete,
    });

    const statusText = getToolStatusText(status, t, {
        ready: t('voterRegistrationForm.statusReady'),
        success: t('voterRegistrationForm.statusSuccess'),
    });

    const howToSteps = useMemo(
        () => [1, 2, 3, 4].map((i) => t(`voterRegistrationForm.howTo.step${i}`)),
        [t],
    );

    const fieldError = (key) => numberErrors[key] || (fieldErrors[key] ? ' ' : undefined);

    const textField = (key, extra = {}) => (
        <div onFocusCapture={() => setFocusField(key)}>
            <FormLabel htmlFor={key}>{t(`voterRegistrationForm.fields.${key}`)}</FormLabel>
            <FormInput
                id={key}
                name={key}
                value={form[key]}
                onChange={(v) => update(key, v)}
                placeholder={t(`voterRegistrationForm.placeholders.${key}`)}
                error={fieldError(key)}
                {...extra}
            />
        </div>
    );

    /**
     * Division / district / upazila as linked dropdowns (see shared/bdGeo): the
     * first two pick from the list, upazila also takes a typed name.
     */
    const geoField = (key, geo, { selectOnly = false } = {}) => (
        <div onFocusCapture={() => setFocusField(key)}>
            <FormLabel htmlFor={key}>{t(`voterRegistrationForm.fields.${key}`)}</FormLabel>
            <GeoSelectField
                {...geo}
                selectOnly={selectOnly}
                placeholder={t(`voterRegistrationForm.placeholders.${key}`)}
                error={fieldError(key)}
                inputClassName={`${inputBaseClass} ${fieldError(key) ? inputErrClass : inputOkClass}`}
            />
        </div>
    );

    const dateField = (key, { fromYear = BIRTH_FROM_YEAR } = {}) => (
        <div onFocusCapture={() => setFocusField(key)}>
            <FormLabel htmlFor={key}>{t(`voterRegistrationForm.fields.${key}`)}</FormLabel>
            <DatePickerField
                value={parseDateField(form[key])}
                onChange={(date) => {
                    update(key, formatDateField(date));
                    if (key === 'birthDate' && isUnderVoterAge(date)) setAgeNotice({ mode: 'picked', date });
                }}
                formatValue={formatDateField}
                placeholder={t(`voterRegistrationForm.placeholders.${key}`)}
                triggerClassName={`${inputBaseClass} ${fieldError(key) ? inputErrClass : inputOkClass}`}
                fromYear={fromYear}
                toYear={TODAY.getFullYear()}
                disabled={{ after: TODAY }}
                clearable
            />
        </div>
    );

    const digitField = (key, maxLength, extra = {}) => (
        <div onFocusCapture={() => setFocusField(key)}>
            <FormLabel htmlFor={key}>{t(`voterRegistrationForm.fields.${key}`)}</FormLabel>
            <FormInput
                id={key}
                name={key}
                value={form[key]}
                onChange={(v) => handleDigitsChange(key, v, maxLength)}
                onBlur={() => markTouched(key)}
                placeholder={t(`voterRegistrationForm.placeholders.${key}`)}
                error={fieldError(key)}
                inputMode="numeric"
                maxLength={maxLength}
                {...extra}
            />
        </div>
    );

    const mobileField = (key) => (
        <div onFocusCapture={() => setFocusField(key)}>
            <FormLabel htmlFor={key}>{t(`voterRegistrationForm.fields.${key}`)}</FormLabel>
            <FormInput
                id={key}
                name={key}
                value={form[key]}
                onChange={(v) => handlePhoneChange(key, v)}
                onBlur={() => markTouched(key)}
                placeholder={t(`voterRegistrationForm.placeholders.${key}`)}
                error={fieldError(key)}
                inputMode="tel"
                type="tel"
                maxLength={11}
                autoComplete="tel"
            />
        </div>
    );

    const checkboxField = (key) => (
        <label
            onFocusCapture={() => setFocusField(key)}
            className="flex items-center gap-2 text-[12.5px] text-[#3D3833] dark:text-zinc-200">
            <input
                type="checkbox"
                checked={form[key]}
                onChange={(e) => update(key, e.target.checked)}
                className="h-4 w-4 rounded border-[#ECE8E4] accent-purple-600 dark:border-zinc-700"
            />
            {t(`voterRegistrationForm.fields.${key}`)}
        </label>
    );

    /** One of the form's own tick strips, as a row of chips. */
    const choiceField = (key, options, optionRoot) => (
        <div onFocusCapture={() => setFocusField(key)}>
            <FormLabel>{t(`voterRegistrationForm.fields.${key}`)}</FormLabel>
            <div className="flex flex-wrap gap-2">
                {options.map((option) => {
                    const active = form[key] === option;
                    return (
                        <button
                            key={option}
                            type="button"
                            // Tapping the chosen one again clears it, so the sheet
                            // can still print with nothing ticked.
                            onClick={() => update(key, active ? '' : option)}
                            className={`rounded-xl border px-3 py-2 text-[13px] font-semibold transition-colors ${
                                active
                                    ? 'border-purple-400 bg-purple-50 text-purple-700 dark:border-purple-500 dark:bg-purple-950/30 dark:text-purple-300'
                                    : 'border-[#ECE8E4] bg-white text-[#3D3833] hover:border-purple-300 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200'
                            }`}
                        >
                            {t(`voterRegistrationForm.${optionRoot}.${option}`)}
                        </button>
                    );
                })}
            </div>
        </div>
    );

    /** Items ২৪ and ২৫ ask for the same nine lines, so they are built once. */
    const addressBlock = (prefix) => {
        const geo = geoCascade(form, update, {
            division: `${prefix}Division`,
            district: `${prefix}District`,
            upazila: `${prefix}Upazila`,
        });
        return (
        <div className="space-y-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {geoField(`${prefix}Division`, geo.division, { selectOnly: true })}
                {geoField(`${prefix}District`, geo.district, { selectOnly: true })}
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {geoField(`${prefix}Upazila`, geo.upazila)}
                {textField(`${prefix}Ward`)}
            </div>
            {textField(`${prefix}LocalBody`)}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {textField(`${prefix}Mouza`)}
                {textField(`${prefix}Village`, { autoComplete: 'street-address' })}
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {textField(`${prefix}Holding`)}
                {textField(`${prefix}PostOffice`, { autoComplete: 'address-line2' })}
            </div>
            {digitField(`${prefix}PostCode`, 4, { autoComplete: 'postal-code' })}
            {choiceField(`${prefix}Rmo`, RMO_TYPES, 'rmoTypes')}
        </div>
        );
    };

    return (
        <>
        <UnderageNoticeDialog
            open={Boolean(ageNotice)}
            birthDate={ageNotice?.date ?? null}
            confirmLabel={ageNotice?.mode === 'download'
                ? t('underageNotice.confirmDownload')
                : ageNotice?.mode === 'print' ? t('underageNotice.confirmPrint') : undefined}
            onCancel={() => setAgeNotice(null)}
            onConfirm={() => {
                const mode = ageNotice?.mode;
                setAgeNotice(null);
                if (mode === 'print') { void runPrint(); return; }
                if (mode !== 'download') return;
                if (emptyFields.length) setConfirmingDownload(true);
                else runDownloadPdf();
            }}
        />
        <MissingFieldsDialog
            open={confirmingDownload}
            fields={emptyFields}
            loading={downloading}
            onCancel={() => setConfirmingDownload(false)}
            onConfirm={confirmDownloadPdf}
        />
        <ToolPageLayout
            slug="general/voter-registration-form"
            subtitleKey="voterRegistrationForm.subtitle"
            // 30/70: the sheet is what the applicant is checking as they type, and
            // an A4 page needs the room to stay legible, so the fields take the
            // narrow column and the preview becomes the sticky rail beside them.
            // The 9px is half the column gap.
            variant="wideControls"
            railWidthClass="lg:w-[calc(70%_-_9px)]"
            status={status}
            statusText={statusText}
            controlsTitle={t('voterRegistrationForm.settingsTitle')}
            howToSteps={howToSteps}
            onReset={hasAnyInput ? handleClear : undefined}
            resetLabel={t('voterRegistrationForm.clearAll')}
            primaryAction={{
                label: t('voterRegistrationForm.downloadPdf'),
                onClick: handleDownloadPdf,
                disabled: printing,
                loading: downloading,
                icon: IconDownload,
            }}
            // Restore and Print ride in the sticky bar with Download and Clear,
            // so every action on the form is in one place at the foot of the
            // screen rather than parked beside a column you have scrolled past.
            secondaryAction={[
                hasSavedSnapshot ? {
                    key: 'restore',
                    label: t('voterRegistrationForm.restoreLast'),
                    onClick: handleRestoreSnapshot,
                    disabled: downloading || printing,
                    icon: IconRefresh,
                } : null,
                {
                    key: 'print',
                    label: printing ? t('toolPage.processing') : t('voterRegistrationForm.print'),
                    onClick: handlePrint,
                    disabled: downloading || printing,
                    icon: IconPrinter,
                },
            ]}
            controls={(
                <div className="space-y-3 font-[Kalpurush,'Noto_Sans_Bengali',sans-serif]">
                    <FormSection
                        number={t('voterRegistrationForm.sections.area.number')}
                        title={t('voterRegistrationForm.sections.area.title')}
                        hint={t('voterRegistrationForm.sections.area.hint')}
                    >
                        <div className="space-y-3">
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                {textField('voterAreaName')}
                                {digitField('voterAreaNo', 4)}
                            </div>
                            {digitField('formNo', 12)}
                        </div>
                    </FormSection>

                    <FormSection
                        number={t('voterRegistrationForm.sections.applicant.number')}
                        title={t('voterRegistrationForm.sections.applicant.title')}
                    >
                        <div className="space-y-3">
                            {textField('nameBn', { autoComplete: 'name' })}
                            {textField('nameEn', { autoComplete: 'name' })}
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                {dateField('birthDate')}
                                {/* Item ১৬ asks for the district of birth — the full district list. */}
                                {geoField('birthPlace', geoCascade(form, update, { district: 'birthPlace' }).district, { selectOnly: true })}
                            </div>
                            {digitField('birthRegNo', 17)}
                            {choiceField('gender', GENDERS, 'genders')}
                            {choiceField('maritalStatus', MARITAL_STATUSES, 'maritalStatuses')}
                        </div>
                    </FormSection>

                    <FormSection
                        number={t('voterRegistrationForm.sections.parents.number')}
                        title={t('voterRegistrationForm.sections.parents.title')}
                        hint={t('voterRegistrationForm.sections.parents.hint')}
                    >
                        <div className="space-y-3">
                            {textField('fatherName', { autoComplete: 'name' })}
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                {digitField('fatherNid', 17, { disabled: form.fatherNidNa })}
                                {digitField('fatherVoterNo', 12)}
                            </div>
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                {digitField('fatherDeathYear', 4)}
                                <div className="flex items-end pb-2">{checkboxField('fatherNidNa')}</div>
                            </div>
                            {textField('motherName', { autoComplete: 'name' })}
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                {digitField('motherNid', 17, { disabled: form.motherNidNa })}
                                {digitField('motherVoterNo', 12)}
                            </div>
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                {digitField('motherDeathYear', 4)}
                                <div className="flex items-end pb-2">{checkboxField('motherNidNa')}</div>
                            </div>
                        </div>
                    </FormSection>

                    <FormSection
                        number={t('voterRegistrationForm.sections.spouse.number')}
                        title={t('voterRegistrationForm.sections.spouse.title')}
                        hint={t('voterRegistrationForm.sections.spouse.hint')}
                    >
                        <div className="space-y-3">
                            {choiceField('spouseKind', SPOUSE_KINDS, 'spouseKinds')}
                            {textField('spouseName', { autoComplete: 'name' })}
                            {digitField('spouseNid', 17, { disabled: form.spouseNidNa })}
                            {digitField('spouseDeathYear', 4)}
                            <div className="space-y-1.5">
                                {checkboxField('spouseNameNa')}
                                {checkboxField('spouseNidNa')}
                                {checkboxField('spouseDeathNa')}
                            </div>
                        </div>
                    </FormSection>

                    <FormSection
                        number={t('voterRegistrationForm.sections.profile.number')}
                        title={t('voterRegistrationForm.sections.profile.title')}
                    >
                        <div className="space-y-3">
                            {textField('education')}
                            {textField('occupation')}
                            {textField('identificationMark')}
                            {choiceField('bloodGroup', BLOOD_GROUPS, 'bloodGroups')}
                            {choiceField('religion', RELIGIONS, 'religions')}
                            {form.religion === 'other' ? textField('religionOther') : null}
                            {choiceField('disability', DISABILITIES, 'disabilities')}
                            {form.disability === 'other' ? textField('disabilityOther') : null}
                        </div>
                    </FormSection>

                    <FormSection
                        number={t('voterRegistrationForm.sections.currentAddress.number')}
                        title={t('voterRegistrationForm.sections.currentAddress.title')}
                    >
                        {addressBlock('cur')}
                    </FormSection>

                    <FormSection
                        number={t('voterRegistrationForm.sections.permanentAddress.number')}
                        title={t('voterRegistrationForm.sections.permanentAddress.title')}
                    >
                        {addressBlock('perm')}
                    </FormSection>

                    <FormSection
                        number={t('voterRegistrationForm.sections.contact.number')}
                        title={t('voterRegistrationForm.sections.contact.title')}
                    >
                        <div className="space-y-3">
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                {mobileField('mobile')}
                                {mobileField('telephone')}
                            </div>
                            {digitField('tin', 14)}
                            {digitField('drivingLicense', 14)}
                            {digitField('passport', 14)}
                        </div>
                    </FormSection>

                    <FormSection
                        number={t('voterRegistrationForm.sections.office.number')}
                        title={t('voterRegistrationForm.sections.office.title')}
                        hint={t('voterRegistrationForm.sections.office.hint')}
                    >
                        <div className="space-y-3">
                            {textField('excludedReason')}
                            {digitField('identifierNid', 17)}
                            {digitField('collectorNid', 17)}
                        </div>
                    </FormSection>
                </div>
            )}
            preview={(
                // A flex column, not a plain block: the preview panel stretches
                // itself, and inside a block wrapper that height would resolve
                // against nothing.
                <div className="flex flex-col gap-3.5 lg:sticky lg:top-4">
                    <ToolPreviewPanel
                        title={t('voterRegistrationForm.previewTitle')}
                        status={status}
                        statusLabel={t(`toolPage.status.${status}`)}
                        idleTitle={t('voterRegistrationForm.previewEmpty')}
                        idleHint={t('voterRegistrationForm.previewEmptyHint')}
                        showIdle={false}
                        tall
                        flushContent
                        headerAction={<PagePreviewZoom {...pageZoom} />}
                    >
                        {/* Bounded, not min-height: a card taller than the screen
                            cannot stay pinned, so the pages scroll inside it. */}
                        <div className="flex h-[min(1100px,calc(100vh-186px))] min-h-[420px] flex-col">
                            <div className="mb-2 flex items-center gap-1.5 text-[12px] font-semibold text-[#6B635C] dark:text-zinc-400">
                                <IconId size={14} stroke={1.8} />
                                {t('voterRegistrationForm.livePreview')}
                            </div>
                            <VoterRegistrationPreviewFrame
                                form={form}
                                previewRef={previewRef}
                                zoom={pageZoom.zoom}
                                focusField={focusField}
                            />
                        </div>
                    </ToolPreviewPanel>
                </div>
            )}
            belowWorkArea={<VoterRegistrationFormInfoBox />}
        />
        </>
    );
}
